import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import lodashCloneDeep from "lodash/cloneDeep";
import lodashFind from "lodash/find";
import lodashRemove from "lodash/remove";
import lodashForOwn from "lodash/forOwn";

import {
  ENDPOINT_CR_MAP_LAYERS,
  ENDPOINT_RISK_CATEGORIES,
  ENDPOINT_RISK_RATING_CATEGORIES,
  ENDPOINT_EVACUATION_LEVELS,
  ENDPOINT_WOD,
} from "@/common/constants/api";
import {
  RISK_LEVELS,
  COUNTRY_PROFILES,
  RISK_RATING_PRIMARIES,
  RISK_RATING_SUB,
  TRAVEL_RISK_RATING_ID,
  MARITIME_MAP_TILE_LAYER_INFO,
} from "@/common/constants/controlRisks/risks";
import { LAYER_ID_ACETATE } from "@/common/constants/maps";
import { httpGet } from "@/services/api.service";
import { addLayer, removeLayer } from "@/common/maps/layers";
import { addSource, removeSource } from "@/common/maps/sources";
import {
  layerClickEventHandlerOn,
  layerClickEventHandlerOff,
} from "@/common/maps/eventHandler";
import { waitForMapReady } from "@/common/maps/maps";
import { getFormatted, nowUtc, daysAgo30Utc } from "@/common/date.utilities";
import { propertyByLanguage } from "@/common/api.utilities";
import {
  upperCaseFirstLetter,
  addTargetToLinks,
} from "@/common/basic.utilities";
import {
  hasPlusCyberAccess,
  hasInternationalSOSAccess,
  hasGrdAccess,
  hasMaritimeAccess,
} from "@/services/user/user.service";
import { isFeatureUnlocked } from "@/common/featureLock.js";

const SOURCE_ID = "mapbox-terrain";
const LOW_OPACITY = 0.25;
const MID_OPACITY = 0.5;
const HIGH_OPACITY = 0.75;

let layersSourceUrl;
let mapLayers;
const ZONE_ID_FIELD = "Zone_ID";
const COUNTRY_ID_FIELD = "ISO";
const WATER_ID_FIELD = "CR_ID";

export { _getMapLayers as getMapLayers };
export { _getRiskRatings as getRiskRatings };
export { _getRiskRatingsByCountryCode as getRiskRatingsByCountryCode };
export { _getRiskRatingsByWaterCode as getRiskRatingsByWaterCode };
export { _getCountryDetails as getCountryDetails };
export { _getWaterDetails as getWaterDetails };
export { _getCountryUpdateArticles as getCountryUpdateArticles };
export { _addToMap as addToMap };
export { _removeFromMap as removeFromMap };
export { _getMapTypeBySelectedRatings as getMapTypeBySelectedRatings };
export { _populateDefaultPreferences as populateDefaultPreferences };
export { _highlightZoneOnMap as highlightZoneOnMap };
export { _getRiskCategoriesConfig as getRiskCategoriesConfig };
export { _getRiskCategories as getRiskCategories };
export { _getRiskCategoriesByGroup as getRiskCategoriesByGroup };
export { _getRiskRatingCategories as getRiskRatingCategories };
export { _getEvacuationLevelsConfig as getEvacuationLevelsConfig };
export { _getEvacuationLevels as getEvacuationLevels };
export { _isRiskCategorySelected as isRiskCategorySelected };
export { _isSubCategorySelected as isSubCategorySelected };
export { _modifyRiskCategoryString as modifyRiskCategoryString };
export { _getRiskCategoryByName as getRiskCategoryByName };

let riskCategoriesConfig = null;
let riskRatingCategoriesConfig = null;
let evacuationLevelsConfig = null;

const _getMapLayers = async () => {
  try {
    if (mapLayers && layersSourceUrl) {
      return true;
    }

    const RESULT = await httpGet({
      url: ENDPOINT_CR_MAP_LAYERS,
      xApiKey: true,
    });
    const { data } = JSON.parse(RESULT);

    if (data) {
      mapLayers = [];
      let sources = [];

      if (isFeatureUnlocked("risks-maritime") && hasMaritimeAccess()) {
        // manually add maritime layer to data array
        data.push(MARITIME_MAP_TILE_LAYER_INFO);
      }

      data.forEach((layer) => {
        if (
          lodashGet(layer, "tilesetId") &&
          lodashGet(layer, "type") &&
          lodashGet(layer, "layerName")
        ) {
          sources.push(layer.tilesetId);

          if (layer.type === "zone") {
            layer.idField = ZONE_ID_FIELD;
            layer.type = "zones";
          } else if (layer.type === "water") {
            layer.idField = WATER_ID_FIELD;
            layer.type = "waters";
          } else {
            layer.idField = COUNTRY_ID_FIELD;
            layer.type = "countries";
          }
          layer.name = layer.layerName;
          delete layer.layerName;
          mapLayers.push(layer);
        }
      });

      layersSourceUrl = `mapbox://${sources.join()}`;
    }
    return true;
  } catch (error) {
    Promise.reject(
      "Issue getting map layers in risks.service.js: " + JSON.stringify(error)
    );
    return null;
  }
};

const _getRiskRatingsByCountryCode = async (countryCode) => {
  return await _getRiskRatings(countryCode);
};

const _getRiskRatingsByWaterCode = async (waterCode) => {
  return await _getRiskRatings(waterCode);
};

const _getRiskRatings = async (
  countryCode = null,
  includeZones = true,
  includeWaters = true,
  includePorts = true
) => {
  try {
    const countryValue = countryCode ? `/${countryCode.toUpperCase()}` : ``;
    const url = `${ENDPOINT_WOD}/risk-rating${countryValue}`;
    const result = await httpGet({ url });
    const { data } = JSON.parse(result);
    if (lodashGet(data, "features")) {
      const features = data.features;
      const zones = [];
      const countries = [];
      const waters = [];
      const ports = [];
      features.forEach((feature) => {
        if (lodashGet(feature, "properties.ratings")) {
          feature.properties.ratings.forEach((rating) => {
            if (rating.riskCategoryName) {
              rating.riskCategoryNameDisplay = upperCaseFirstLetter(
                rating.riskCategoryName
              );
            }
            if (rating.riskCategoryParentName) {
              rating.riskCategoryParentNameDisplay = upperCaseFirstLetter(
                rating.riskCategoryParentName
              );
            }
          });
        }
        if (lodashGet(feature, "properties.isoCode2")) {
          feature.properties.iso2Code = feature.properties.isoCode2;
        }
        if (lodashGet(feature, "properties.type") === "zone" && includeZones) {
          zones.push(feature);
        } else if (
          lodashGet(feature, "properties.type") === "water" &&
          includeWaters
        ) {
          waters.push(feature);
        } else if (
          lodashGet(feature, "properties.type") === "port" &&
          includePorts
        ) {
          feature.properties.code = feature.properties.code
            ? feature.properties.code
            : feature.properties.id;
          ports.push(feature);
        } else if (lodashGet(feature, "properties.type") === "country") {
          countries.push(feature);
        }
      });
      const riskRatings = { countries };
      if (includeZones) {
        riskRatings.zones = zones;
      }
      if (includeWaters) {
        riskRatings.waters = waters;
      }
      if (includePorts) {
        riskRatings.ports = ports;
      }
      return riskRatings;
    }

    return null;
  } catch (error) {
    Promise.reject(
      "Issue getting risks in risks.service.js: " + JSON.stringify(error)
    );
    return null;
  }
};

const _getCountryDetails = async (countryCode) => {
  try {
    const url = `${ENDPOINT_WOD}/country-background/${countryCode.toUpperCase()}`;
    const result = await httpGet({ url });
    const { data } = JSON.parse(result);

    if (lodashGet(data, "features[0]")) {
      const feature = data.features[0];
      await _formatDetails(feature);
      return feature;
    }

    return false;
  } catch (error) {
    Promise.reject(
      "Issue getting country background in risks.service.js: " +
        JSON.stringify(error)
    );
    return null;
  }
};

const _formatDetails = async (feature) => {
  const riskTypes = await _getRiskRatingCategories();
  if (feature?.properties) {
    feature.properties.formatted = {};
    feature.properties.formatted.language = "en";
    if (lodashGet(feature, "properties.id")) {
      feature.properties.formatted.id = feature.properties.id;
    }
    if (lodashGet(feature, "properties.lang")) {
      feature.properties.formatted.language = feature.properties.lang;
      const language = getLanguage(feature.properties.lang);
      if (language?.name) {
        feature.properties.formatted.originalLanguage = language.name;
      }
    }
    if (lodashGet(feature, "properties.title")) {
      feature.properties.formatted.title = propertyByLanguage(
        feature.properties.title,
        feature.properties.formatted.language
      );
    }
    if (lodashGet(feature, "properties.body")) {
      feature.properties.formatted.body = propertyByLanguage(
        feature.properties.body,
        feature.properties.formatted.language
      );
      feature.properties.formatted.body = addTargetToLinks(
        feature.properties.formatted.body
      );
    }
    if (lodashGet(feature, "properties.bodyItems")) {
      const bodyItems = feature.properties.bodyItems;
      bodyItems.forEach((item) => {
        if (lodashGet(item, "title")) {
          item.title = propertyByLanguage(
            item.title,
            feature.properties.formatted.language
          );
        }
        if (lodashGet(item, "body")) {
          item.bodyOriginal = item.body;
          item.body = propertyByLanguage(
            item.body,
            feature.properties.formatted.language
          );
          item.body = addTargetToLinks(item.body);
        }
        if (lodashGet(item, "sanitized_body")) {
          item.sanitizedBody = propertyByLanguage(
            item.sanitized_body,
            feature.properties.formatted.language
          );
        }
        if (lodashGet(item, "publishDate")) {
          item.publishDate = getFormatted(item.publishDate);
        }
        const types = [];
        if (lodashGet(item, "risks")) {
          item.risks.forEach((type) => {
            const riskType = lodashFind(riskTypes, ["id", type.id]);
            if (riskType) {
              types.push(riskType);
            }
          });
        }
        item.riskTypes = types;
      });
      const evacBody = lodashFind(bodyItems, ["title", "Evacuation Watch"]);
      feature.properties.formatted.evacuation = lodashCloneDeep(evacBody);
      lodashRemove(bodyItems, evacBody);
      feature.properties.formatted.bodyItems = bodyItems;
    }
    if (lodashGet(feature, "properties.countryCode")) {
      feature.properties.formatted.countryCode = feature.properties.countryCode;
    }
    if (lodashGet(feature, "properties.countryId")) {
      feature.properties.formatted.countryId = feature.properties.countryId;
    }
    if (lodashGet(feature, "properties.evacuationLevel")) {
      feature.properties.formatted.evacuationLevel =
        feature.properties.evacuationLevel;
    }
    if (lodashGet(feature, "properties.evacuationLevelId")) {
      feature.properties.formatted.evacuationLevelId =
        feature.properties.evacuationLevelId;
    }
    if (lodashGet(feature, "properties.labeled_categories")) {
      feature.properties.formatted.labeledCategories =
        feature.properties.labeled_categories;
    }
    if (lodashGet(feature, "properties.location")) {
      feature.properties.formatted.location = feature.properties.location;
    }
    if (lodashGet(feature, "properties.name")) {
      feature.properties.formatted.name = propertyByLanguage(
        feature.properties.name,
        feature.properties.formatted.language
      );
    }
    if (lodashGet(feature, "properties.publishDate")) {
      feature.properties.formatted.publishDate = getFormatted(
        feature.properties.publishDate
      );
    }
    if (lodashGet(feature, "properties.publishedDate")) {
      feature.properties.formatted.publishDate = getFormatted(
        feature.properties.publishedDate
      );
    }
    if (lodashGet(feature, "properties.regionCode")) {
      feature.properties.formatted.regionCode = feature.properties.regionCode;
    }
    if (lodashGet(feature, "properties.regionId")) {
      feature.properties.formatted.regionId = feature.properties.regionId;
    }
    if (lodashGet(feature, "properties.isoCode2")) {
      feature.properties.iso2Code = feature.properties.isoCode2;
      feature.properties.formatted.iso2Code = feature.properties.isoCode2;
    }
    if (lodashGet(feature, "properties.riskContent")) {
      const riskContentFormatted = [];
      feature.properties.riskContent.forEach((item) => {
        const types = [];
        if (lodashGet(item, "risks")) {
          item.riskTypesOriginal = lodashCloneDeep(item.risks);
          item.risks.forEach((type) => {
            const riskType = lodashFind(riskTypes, ["id", type.id]);
            if (lodashGet(riskType, "name")) {
              types.push(riskType.name);
            }
          });
        }
        item.riskTypes = types;
        if (lodashGet(item, "name")) {
          item.nameOriginal = lodashCloneDeep(item.name);
          item.name = propertyByLanguage(
            item.name,
            feature.properties.formatted.language
          );
        }
        if (lodashGet(item, "sanitizedBody")) {
          item.sanitizedBodyOriginal = lodashCloneDeep(item.sanitizedBody);
          item.sanitizedBody = propertyByLanguage(
            item.sanitized_body,
            feature.properties.formatted.language
          );
        }
        if (lodashGet(item, "title")) {
          item.titleOriginal = lodashCloneDeep(item.title);
          item.title = propertyByLanguage(
            item.title,
            feature.properties.formatted.language
          );
        }
        if (lodashGet(item, "body")) {
          item.bodyOriginal = lodashCloneDeep(item.body);
          item.body = propertyByLanguage(
            item.body,
            feature.properties.formatted.language
          );
          item.body = addTargetToLinks(item.body);
        }
        if (lodashGet(item, "publishDate")) {
          item.publishDateOriginal = item.publishDate;
          item.publishDate = getFormatted(item.publishDate);
        }
        if (!item.risks || item.risks?.length === types?.length) {
          riskContentFormatted.push(item);
        }
      });
      feature.properties.formatted.riskContent = riskContentFormatted;

      // find country profiles for political system and economic in riskContent
      const political = lodashFind(feature.properties.formatted.riskContent, [
        "name",
        COUNTRY_PROFILES.political.name,
      ]);
      const economic = lodashFind(feature.properties.formatted.riskContent, [
        "name",
        COUNTRY_PROFILES.economic.name,
      ]);
      const profiles = [];
      if (political) {
        profiles.push(political);
      }
      if (economic) {
        profiles.push(economic);
      }
      feature.properties.formatted.profiles = profiles;
    }
    if (lodashGet(feature, "properties.sanitizedBody")) {
      feature.properties.formatted.sanitizedBody = propertyByLanguage(
        feature.properties.sanitizedBody,
        feature.properties.formatted.language
      );
    }
    if (lodashGet(feature, "properties.source")) {
      feature.properties.formatted.source = feature.properties.source;
    }
    if (lodashGet(feature, "properties.scenarios")) {
      feature.properties.scenarios.forEach((item) => {
        if (lodashGet(item, "title")) {
          item.titleOriginal = lodashCloneDeep(item.title);
          item.title = propertyByLanguage(
            item.title,
            feature.properties.formatted.language
          );
        }
        if (lodashGet(item, "overview")) {
          item.overviewOriginal = lodashCloneDeep(item.overview);
          item.overview = propertyByLanguage(
            item.overview,
            feature.properties.formatted.language
          );
          item.overview = addTargetToLinks(item.overview);
        }
        if (lodashGet(item, "sanitizedOverview")) {
          item.sanitizedOverviewOriginal = lodashCloneDeep(
            item.sanitizedOverview
          );
          item.sanitizedOverview = propertyByLanguage(
            item.sanitizedOverview,
            feature.properties.formatted.language
          );
        }
        if (lodashGet(item, "scenarioDevelopments")) {
          item.scenarioDevelopmentsOriginal = lodashCloneDeep(
            item.scenarioDevelopments
          );
          item.scenarioDevelopments = propertyByLanguage(
            item.scenarioDevelopments,
            feature.properties.formatted.language
          );
          item.scenarioDevelopments = addTargetToLinks(
            item.scenarioDevelopments
          );
        }
        if (lodashGet(item, "sanitizedScenarioDevelopments")) {
          item.sanitizedScenarioDevelopmentsOriginal = lodashCloneDeep(
            item.sanitizedScenarioDevelopments
          );
          item.sanitizedScenarioDevelopments = propertyByLanguage(
            item.sanitizedScenarioDevelopments,
            feature.properties.formatted.language
          );
        }
        if (lodashGet(item, "publishDate")) {
          item.publishDateOriginal = lodashCloneDeep(item.publishDate);
          item.publishDate = getFormatted(item.publishDate);
        }
        if (lodashGet(item, "specificIssues")) {
          item.specificIssuesOriginal = lodashCloneDeep(item.specificIssues);
          item.specificIssues.forEach((issue) => {
            if (lodashGet(issue, "summary")) {
              issue.summaryOriginal = lodashCloneDeep(issue.summary);
              issue.summary = propertyByLanguage(
                issue.summary,
                feature.properties.formatted.language
              );
            }
            if (lodashGet(issue, "risk_type_id")) {
              issue.riskTypeId = issue.risk_type_id;
              const riskType = lodashFind(riskTypes, ["id", issue.riskTypeId]);
              if (lodashGet(riskType, "name")) {
                issue.riskTypeName = upperCaseFirstLetter(riskType.name);
              }
            }
            if (lodashGet(issue, "rate_value")) {
              issue.rateValue = issue.rate_value;
            }
          });
        }
      });
      feature.properties.formatted.scenarios = feature.properties.scenarios;
    }
    if (lodashGet(feature, "properties.ports")) {
      const ports = [];
      feature.properties.ports.forEach((item) => {
        if (lodashGet(item, "title")) {
          item.title = propertyByLanguage(
            item.title,
            feature.properties.formatted.language
          );
        }
        if (lodashGet(item, "content_body")) {
          item.bodyOriginal = item.content_body;
          item.body = propertyByLanguage(
            item.content_body,
            feature.properties.formatted.language
          );
          item.body = addTargetToLinks(item.body);
        }
        if (lodashGet(item, "content_sanitized_body")) {
          item.sanitizedBody = propertyByLanguage(
            item.content_sanitized_body,
            feature.properties.formatted.language
          );
        }
        const waters = [];
        if (lodashGet(item, "waters")) {
          item.waters.forEach((water) => {
            waters.push({ code: water.code, type: water.type });
          });
        }
        item.waters = waters;

        // To have WB/ES compatibility
        if (item.id && !item.code) {
          item.code = item.id;
        }

        if (item.title && !item.name) {
          item.name = item.title;
        }

        if (item.body && !item.overview) {
          item.overview = [
            {
              body: item.body,
              type: "overview",
            },
          ];
        }

        ports.push(item);
      });
      feature.properties.formatted.ports = ports;
    }
  }
};

const _getWaterDetails = async (waterCode) => {
  try {
    const url = `${ENDPOINT_WOD}/water-background/${waterCode.toUpperCase()}`;
    const result = await httpGet({ url });
    const { data } = JSON.parse(result);

    if (lodashGet(data, "waterBackground")) {
      return data.waterBackground;
    }

    return false;
  } catch (error) {
    Promise.reject(
      "Issue getting water background in risks.service.js: " +
        JSON.stringify(error)
    );
    return null;
  }
};

const _getCountryUpdateArticles = async (countryCode, category) => {
  try {
    const categories =
      category && category.riskCategoryId
        ? `&riskCategories=${category.riskCategoryId}`
        : "";

    const start = daysAgo30Utc();
    const end = nowUtc();

    const url = `${ENDPOINT_WOD}?aoiId=${countryCode}${categories}&sources=analysis&start=${start}&end=${end}`;

    const RESULT = await httpGet({ url });
    const { data } = JSON.parse(RESULT);

    if (lodashGet(data, "features")) {
      data.features.forEach(async (feature) => {
        await _formatDetails(feature);
      });
      return data.features;
    }

    return false;
  } catch (error) {
    Promise.reject(
      "Issue getting country update articles in risks.service.js: " +
        JSON.stringify(error)
    );
    return null;
  }
};

const _formatData = async (data) => {
  let flattenedData = {
    countries: {},
    zones: {},
    waters: {},
  };

  const riskRatingCategories = await _getRiskRatingCategories();

  if (data && data.countries && data.countries.length && !data.waters) {
    const lenCountries = data.countries.length;

    riskRatingCategories.forEach((riskRating) => {
      flattenedData.countries[riskRating.id] = [];
    });

    for (let i = 0; i < lenCountries; i++) {
      const country = data.countries[i];
      if (
        lodashGet(country, "properties.ratings") &&
        lodashGet(country, "properties.code")
      ) {
        await _getRatingsForCountry(country, flattenedData);
      }
    }
  }

  if (data && data.zones && data.zones.length && !data.waters) {
    const lenZones = data.zones.length;

    riskRatingCategories.forEach((riskRating) => {
      flattenedData.zones[riskRating.id] = [];
    });

    for (let i = 0; i < lenZones; i++) {
      const zone = data.zones[i];
      zone.properties.id = 1 + i;

      if (
        lodashGet(zone, "properties.ratings") &&
        lodashGet(zone, "properties.code")
      ) {
        await _getRatingsForZone(zone, flattenedData);
      }
    }
  }

  if (data && data.waters && data.waters.length) {
    riskRatingCategories.forEach((riskRating) => {
      flattenedData.waters[riskRating.id] = [];
    });

    // handle international waters
    const lenWaters = data.waters.length;
    for (let i = 0; i < lenWaters; i++) {
      const water = data.waters[i];
      water.properties.id = 1 + i;
      if (
        lodashGet(water, "properties.ratings") &&
        lodashGet(water, "properties.code")
      ) {
        await _getRatingsForWater(water, flattenedData);
      }
    }

    // handle national waters
    const lenCountries = data.countries.length;
    for (let i = 0; i < lenCountries; i++) {
      const country = data.countries[i];
      if (
        lodashGet(country, "properties.ratings") &&
        lodashGet(country, "properties.iso2Code")
      ) {
        const cntry = lodashCloneDeep(country);
        cntry.properties.code = cntry.properties.iso2Code;
        await _getRatingsForWater(cntry, flattenedData);
      }
    }
  }

  return flattenedData;
};

const _getRatingsForCountry = async (country, data) => {
  return await _getRatingsForType({
    country: country,
    data: data,
  });
};

const _getRatingsForZone = async (zone, data) => {
  return await _getRatingsForType({
    zone: zone,
    data: data,
  });
};

const _getRatingsForWater = async (water, data) => {
  return await _getRatingsForType({
    water: water,
    data: data,
  });
};

const _getRatingsForType = async (options) => {
  let type;
  let ratings;
  const riskRatingCategories = await _getRiskRatingCategories();
  let riskRatingsAvailable = riskRatingCategories;
  if (options.zone) {
    type = "zone";
    ratings = options.zone.properties.ratings;
  } else if (options.water) {
    type = "water";
    ratings = options.water.properties.ratings;
    const riskRatingMaritime = [];
    riskRatingCategories.forEach((rr) => {
      if (
        rr.id === RISK_RATING_PRIMARIES.maritime ||
        rr.parentId === RISK_RATING_PRIMARIES.maritime
      ) {
        riskRatingMaritime.push(rr);
      }
    });
    riskRatingsAvailable = riskRatingMaritime;
  } else {
    type = "country";
    ratings = options.country.properties.ratings;
  }
  const lenRatings = ratings.length;
  const lenCategories = riskRatingsAvailable.length;
  for (let i = 0; i < lenRatings; i++) {
    const rating = ratings[i];
    const riskCategoryId = lodashGet(rating, "riskCategoryId");
    if (riskCategoryId) {
      for (let j = 0; j < lenCategories; j++) {
        const currentCategoryId = riskRatingsAvailable[j].id;
        const currentCategoryIdCR = riskRatingsAvailable[j].id;
        if (currentCategoryId === riskCategoryId) {
          const ratingLevel = lodashGet(rating, "rateValue");
          if (ratingLevel) {
            if (type === "country") {
              options.data.countries[currentCategoryIdCR].push([
                options.country.properties.iso2Code,
                ratingLevel,
              ]);
            } else if (type === "zone") {
              options.data.zones[currentCategoryIdCR].push([
                options.zone.properties.code,
                ratingLevel,
              ]);
            } else if (type === "water") {
              options.data.waters[currentCategoryIdCR].push([
                options.water.properties.code,
                ratingLevel,
              ]);
            }
          }
          break;
        }
      }
    }
  }

  // Removing any duplicate zone codes within each zone risk category.
  // For some reason there are duplicate zones codes for some
  // zone risk categories which is causing the map tiles
  // to fail. This seems like something that should be fixed
  // on the data side. Have this chunk of code until that happens.
  lodashForOwn(options.data.zones, (value, key) => {
    const zoneCats = [];
    const zones = [];
    if (value.length) {
      value.forEach((item) => {
        if (!zones.includes(item[0])) {
          zoneCats.push(item);
          zones.push(item[0]);
        }
      });
    }
    options.data.zones[key] = zoneCats;
  });

  // Removing any possible duplicate water codes
  lodashForOwn(options.data.waters, (value, key) => {
    const waterCats = [];
    const waters = [];
    if (value.length) {
      value.forEach((item) => {
        if (!waters.includes(item[0])) {
          waterCats.push(item);
          waters.push(item[0]);
        }
      });
    }
    options.data.waters[key] = waterCats;
  });
};

const getRRListFromRange = (riskScoreRange) => {
  if (!riskScoreRange) {
    riskScoreRange = [1, 2, 3, 4, 5];
  }
  let riskScoresToDisplay = [];
  for (let i = riskScoreRange[0]; i < riskScoreRange[1] + 1; i++) {
    riskScoresToDisplay.push(i);
  }
  return riskScoresToDisplay;
};

const _addToMap = async (params) => {
  await waitForMapReady(params.map);
  _removeFromMap(params);

  let beforeLayer = LAYER_ID_ACETATE;
  if (params.map && params.map.getLayer("City large scale/town small")) {
    beforeLayer = "City large scale/town small";
  }

  let processedData;
  if (params.countries || params.waters) {
    processedData = await _formatData(params);
  }

  let desiredRiskType = 1;
  if (params.riskType) {
    desiredRiskType = params.riskType;
  }

  let riskScoresToDisplay = getRRListFromRange(params.riskScoresToDisplay);

  let stuffToHide = _getAoisToHide(
    processedData,
    desiredRiskType,
    riskScoresToDisplay
  );

  addSource(params.map, SOURCE_ID, {
    type: "vector",
    url: layersSourceUrl,
  });

  if (mapLayers) {
    mapLayers.forEach((layerInfo) => {
      if (
        (((layerInfo.type === "countries" && params.countries) ||
          (layerInfo.type === "zones" && params.zones)) &&
          !params.waters) ||
        (layerInfo.type === "waters" && params.waters)
      ) {
        const expression = _getStyleExpression(
          layerInfo.idField,
          processedData[layerInfo.type][desiredRiskType]
        );
        const opacityExpression = _getOpacityExpression(
          layerInfo.idField,
          stuffToHide,
          riskScoresToDisplay
        );
        const filterExpression = _getFilterExpression(
          layerInfo.idField,
          stuffToHide
        );
        const options = {
          name: layerInfo.name,
          expression: expression,
          opacityExpression: opacityExpression,
          filterExpression: filterExpression,
          beforeLayer: beforeLayer,
        };

        if (params.mapClickEvent) {
          options.mapClickEvent = params.mapClickEvent;
        }

        _makeLayer(params.map, options);
      }
    });
  }
};

const _removeFromMap = (params = {}) => {
  if (!params.map) {
    return;
  }

  if (mapLayers && Array.isArray(mapLayers)) {
    mapLayers.forEach((layer) => {
      layerClickEventHandlerOff(params.map, `${layer.name}-fill`);
      removeLayer(params.map, `${layer.name}-fill`);
    });
    removeSource(params.map, SOURCE_ID);
  }
};

function _makeLayer(map, params) {
  const options = {
    id: `${params.name}-fill`,
    type: "fill",
    source: SOURCE_ID,
    "source-layer": params.name,
    layout: {},
    paint: {
      "fill-color": params.expression,
      "fill-opacity": params.opacityExpression
        ? params.opacityExpression
        : MID_OPACITY,
    },
    maxzoom: 8,
    filter: params.filterExpression,
  };

  addLayer(map, options, params.beforeLayer);

  if (params.mapClickEvent) {
    layerClickEventHandlerOn(map, `${params.name}-fill`, params.mapClickEvent);
  }
}

function _getColorByRiskRating(ratingValue) {
  for (let i = 0; i < RISK_LEVELS.length; i++) {
    if (RISK_LEVELS[i].value === ratingValue) {
      return RISK_LEVELS[i].bgColor;
    }
  }
}

function _getStyleExpression(propName, data) {
  let base = ["match", ["get", propName]];

  if (data) {
    for (let i = 0; i < data.length; i++) {
      base.push(data[i][0]);
      base.push(_getColorByRiskRating(data[i][1]));
    }
  }

  base.push("#000000");
  return base;
}

function _getAoisToHide(data, riskType, riskScoresToShow) {
  let countriesToHide = [];
  let zonesToHide = [];
  let watersToHide = [];
  let countriesToShow = [];
  let zonesToShow = [];
  let watersToShow = [];

  if (riskType && data && data.countries && data.countries[riskType]) {
    for (let i = 0; i < data.countries[riskType].length; i++) {
      if (riskScoresToShow.indexOf(data.countries[riskType][i][1]) > -1) {
        countriesToShow.push(data.countries[riskType][i][0]);
      } else {
        countriesToHide.push(data.countries[riskType][i][0]);
      }
    }
  }

  if (riskType && data && data.zones && data.zones[riskType]) {
    for (let i = 0; i < data.zones[riskType].length; i++) {
      if (riskScoresToShow.indexOf(data.zones[riskType][i][1]) > -1) {
        zonesToShow.push(data.zones[riskType][i][0]);
      } else {
        zonesToHide.push(data.zones[riskType][i][0]);
      }
    }
  }

  if (riskType && data && data.waters && data.waters[riskType]) {
    for (let i = 0; i < data.waters[riskType].length; i++) {
      if (riskScoresToShow.indexOf(data.waters[riskType][i][1]) > -1) {
        watersToShow.push(data.waters[riskType][i][0]);
      } else {
        watersToHide.push(data.waters[riskType][i][0]);
      }
    }
  }

  return {
    countriesToHide,
    zonesToHide,
    watersToHide,
    countriesToShow,
    zonesToShow,
    watersToShow,
  };
}

function _getOpacityExpression(idField, stuffToHide, riskScoresToDisplay) {
  if (riskScoresToDisplay.length === RISK_LEVELS.length) {
    return MID_OPACITY;
  }

  let filterConditionsHide = [];
  let filterConditionsShow = [];

  if (idField === COUNTRY_ID_FIELD) {
    for (let i = 0; i < stuffToHide.countriesToHide.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", COUNTRY_ID_FIELD],
        stuffToHide.countriesToHide[i],
      ]);
    }
    for (let i = 0; i < stuffToHide.countriesToShow.length; i++) {
      filterConditionsShow.push([
        "==",
        ["get", COUNTRY_ID_FIELD],
        stuffToHide.countriesToShow[i],
      ]);
    }
  }

  if (idField === ZONE_ID_FIELD) {
    for (let i = 0; i < stuffToHide.zonesToHide.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", ZONE_ID_FIELD],
        stuffToHide.zonesToHide[i],
      ]);
    }
    for (let i = 0; i < stuffToHide.zonesToShow.length; i++) {
      filterConditionsShow.push([
        "==",
        ["get", ZONE_ID_FIELD],
        stuffToHide.zonesToShow[i],
      ]);
    }
  }

  if (idField === WATER_ID_FIELD) {
    for (let i = 0; i < stuffToHide.watersToHide.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", WATER_ID_FIELD],
        stuffToHide.watersToHide[i],
      ]);
    }
    for (let i = 0; i < stuffToHide.watersToShow.length; i++) {
      filterConditionsShow.push([
        "==",
        ["get", WATER_ID_FIELD],
        stuffToHide.watersToShow[i],
      ]);
    }
  }

  return [
    "case",
    ["any", ...filterConditionsHide],
    LOW_OPACITY,
    ["any", ...filterConditionsShow],
    HIGH_OPACITY,
    MID_OPACITY,
  ];
}

function _getFilterExpression(idField, stuffToHide) {
  let filterConditionsHide = [];

  if (idField === COUNTRY_ID_FIELD) {
    for (let i = 0; i < stuffToHide.countriesToHide.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", COUNTRY_ID_FIELD],
        stuffToHide.countriesToHide[i],
      ]);
    }
    for (let i = 0; i < stuffToHide.countriesToShow.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", COUNTRY_ID_FIELD],
        stuffToHide.countriesToShow[i],
      ]);
    }
  }

  if (idField === ZONE_ID_FIELD) {
    for (let i = 0; i < stuffToHide.zonesToHide.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", ZONE_ID_FIELD],
        stuffToHide.zonesToHide[i],
      ]);
    }
    for (let i = 0; i < stuffToHide.zonesToShow.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", ZONE_ID_FIELD],
        stuffToHide.zonesToShow[i],
      ]);
    }
  }

  if (idField === WATER_ID_FIELD) {
    for (let i = 0; i < stuffToHide.watersToHide.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", WATER_ID_FIELD],
        stuffToHide.watersToHide[i],
      ]);
    }
    for (let i = 0; i < stuffToHide.watersToShow.length; i++) {
      filterConditionsHide.push([
        "==",
        ["get", WATER_ID_FIELD],
        stuffToHide.watersToShow[i],
      ]);
    }
  }

  return ["any", ...filterConditionsHide];
}

async function _getMapTypeBySelectedRatings(selectedRatings) {
  let desiredRiskType = null;
  if (selectedRatings && selectedRatings.id) {
    const riskRatingCategories = await _getRiskRatingCategories();
    const rating = lodashFind(riskRatingCategories, ["id", selectedRatings.id]);
    if (rating && rating.id) {
      desiredRiskType = rating.id;
    }
  }
  return desiredRiskType;
}

function _populateDefaultPreferences(prefs = {}) {
  if (!prefs.controlRisks) {
    prefs.controlRisks = {};
  }
  if (!prefs.controlRisks.riskRatings) {
    prefs.controlRisks.riskRatings = {};
  }

  return prefs;
}

async function _highlightZoneOnMap(params) {
  const LAYER_ID = "zoneHighlight";

  await waitForMapReady(params.map);
  await _getMapLayers();
  addSource(params.map, SOURCE_ID, {
    type: "vector",
    url: layersSourceUrl,
  });

  mapLayers.forEach((layerInfo) => {
    if (layerInfo.idField !== ZONE_ID_FIELD) {
      return;
    }
    try {
      params.map.addLayer({
        id: LAYER_ID,
        type: "line",
        source: SOURCE_ID,
        "source-layer": layerInfo.name,
        layout: {},
        paint: {
          "line-color": "#FFF",
          "line-width": 3,
          "line-opacity": 0,
          "line-opacity-transition": {
            duration: 1000,
            delay: 0,
          },
        },
        filter: ["==", ["get", ZONE_ID_FIELD], params.zoneCode],
      });

      params.map.setPaintProperty(LAYER_ID, "line-opacity", 1);
      setTimeout(() => {
        params.map.setPaintProperty(LAYER_ID, "line-opacity", 0);
      }, 2000);
      setTimeout(() => {
        params.map.removeLayer(LAYER_ID);
      }, 3000);
    } catch (err) {
      console.log(err);
    }
  });
}

const _getRiskCategoriesConfig = async () => {
  let URL = ENDPOINT_RISK_CATEGORIES;
  if (!riskCategoriesConfig) {
    try {
      const RESULT = await httpGet({
        url: URL,
        noCache: true,
      });
      const { data } = JSON.parse(RESULT);
      riskCategoriesConfig = data;
      return riskCategoriesConfig;
    } catch (error) {
      Promise.reject(
        "Issue getting risk categories in risks.service.js: " +
          JSON.stringify(error)
      );
      return null;
    }
  }
};

const _getRiskCategories = async (params) => {
  if (!riskCategoriesConfig) {
    await _getRiskCategoriesConfig();
  }
  return riskCategoriesConfig.filter((category) => {
    if (
      (category.id === RISK_RATING_PRIMARIES.cyber ||
        category.parentId === RISK_RATING_PRIMARIES.cyber) &&
      !hasPlusCyberAccess()
    ) {
      return false;
    }
    if (
      category.id === TRAVEL_RISK_RATING_ID &&
      (!hasInternationalSOSAccess() ||
        !isFeatureUnlocked("risk-category-travel"))
    ) {
      return false;
    }
    if (
      (category.id === RISK_RATING_PRIMARIES.maritime ||
        category.parentId === RISK_RATING_PRIMARIES.maritime) &&
      (!hasMaritimeAccess() || !isFeatureUnlocked("risks-maritime"))
    ) {
      return false;
    }

    if (params && params.ui) {
      return !category.parentId && !category.ui.hidden;
    }
    return !category.parentId;
  });
};

const _getRiskCategoriesByGroup = async (params) => {
  let categories = await _getRiskCategories(params);
  return categories
    .filter((category) => {
      if (params && params.ui) {
        return !category.parentId && !category.ui.hidden;
      }
      return !category.parentId;
    })
    .map((category) => {
      return _appendSubtopics(category);
    });
};

const _getRiskRatingCategoriesConfig = async () => {
  if (!riskRatingCategoriesConfig) {
    try {
      let URL = ENDPOINT_RISK_RATING_CATEGORIES;

      const RESULT = await httpGet({
        url: URL,
        noCache: true,
      });
      const { data } = JSON.parse(RESULT);
      riskRatingCategoriesConfig = data;
      return data;
    } catch (error) {
      Promise.reject(
        "Issue getting risk rating categories in risks.service.js: " +
          JSON.stringify(error)
      );
      return null;
    }
  }
  return lodashCloneDeep(riskRatingCategoriesConfig);
};

const _getRiskRatingCategories = async () => {
  let riskRatingCategories = [];
  riskRatingCategories = await _getRiskRatingCategoriesConfig();
  riskRatingCategories = riskRatingCategories.filter((category) => {
    if (
      (category.id === RISK_RATING_PRIMARIES.cyber ||
        category.parentId === RISK_RATING_PRIMARIES.cyber) &&
      !hasPlusCyberAccess()
    ) {
      return false;
    }
    if (
      (category.id === RISK_RATING_PRIMARIES.maritime ||
        category.parentId === RISK_RATING_PRIMARIES.maritime) &&
      (!hasMaritimeAccess() || !isFeatureUnlocked("risks-maritime"))
    ) {
      return false;
    }
    if (
      hasGrdAccess() &&
      !(
        category.id === RISK_RATING_PRIMARIES.security ||
        category.id === RISK_RATING_SUB.kidnap
      )
    ) {
      return false;
    }
    return true;
  });

  riskRatingCategories.sort((a, b) =>
    a.order.toString().localeCompare(b.order.toString())
  );

  return riskRatingCategories;
};

const _getEvacuationLevelsConfig = async (params, force) => {
  let URL =
    params && params.ui
      ? ENDPOINT_EVACUATION_LEVELS + "?ui=true"
      : ENDPOINT_EVACUATION_LEVELS;
  if (!evacuationLevelsConfig || force) {
    try {
      const RESULT = await httpGet({
        url: URL,
        noCache: true,
      });
      const { data } = JSON.parse(RESULT);

      if (data && Array.isArray(data) && data.length) {
        const language = "en";
        data.forEach((item) => {
          if (lodashGet(item, "name")) {
            item.nameOriginal = lodashCloneDeep(item.name);
            let name = "";
            item.name.forEach((n) => {
              if (n.languageCode === language) {
                name = n.text;
              }
            });
            item.name = name;
            item.level = item.Level;
          }
        });
      }

      evacuationLevelsConfig = data;

      return data;
    } catch (error) {
      Promise.reject(
        "Issue getting evacuation levels in risks.service.js: " +
          JSON.stringify(error)
      );
      return null;
    }
  }
};

const _getEvacuationLevels = (params) => {
  if (params) {
    return _getEvacuationLevelsConfig(params);
  }
  return lodashCloneDeep(evacuationLevelsConfig);
};

const _appendSubtopics = (_category) => {
  let category = JSON.parse(JSON.stringify(_category));
  if (
    !lodashHas(category, "parentId") &&
    (!category.subtopics || !category.subtopics instanceof Array)
  ) {
    category.subtopics = [];
  }
  for (let i = 0; i < riskCategoriesConfig.length; i++) {
    const cur = riskCategoriesConfig[i];
    if (
      lodashHas(cur, "parentId") &&
      cur.parentId === category.id &&
      !cur.ui.hidden
    ) {
      category.subtopics.push(_appendSubtopics(cur));
    }
  }
  return category;
};

const _isRiskCategorySelected = (categoryId, selectionString = "") => {
  let selectedCategories = selectionString ? selectionString.split(",") : [];
  return selectedCategories.indexOf(categoryId) > -1;
};

const _isSubCategorySelected = (riskCategoryId, selectionString = "") => {
  let selectedRiskCategories = selectionString
    ? selectionString.split(",")
    : [];

  let category = riskCategoriesConfig.filter((c) => {
    return c.id === riskCategoryId;
  });

  if (
    lodashHas(category[0], "parentId") &&
    selectedRiskCategories.indexOf(category[0].parentId) > -1
  ) {
    return true;
  }
  return _isRiskCategorySelected(riskCategoryId, selectionString);
};

function _modifyRiskCategoryString(riskCategoryId, enabled, currentSelection) {
  let riskCategory = riskCategoriesConfig.filter((category) => {
    return category.id === riskCategoryId;
  });
  let currentSelectionSplit = currentSelection
    ? currentSelection.split(",")
    : [];

  if (!enabled) {
    if (riskCategory[0].parentId) {
      let id = riskCategory[0].id;
      let index = currentSelectionSplit.indexOf(id);
      if (index > -1) {
        currentSelectionSplit.splice(index, 1);
      }
      let parentIndex = currentSelectionSplit.indexOf(riskCategory[0].parentId);
      if (parentIndex > -1) {
        currentSelectionSplit.splice(parentIndex, 1);
      }
    } else {
      const parent = riskCategory[0];
      let parentIndex = currentSelectionSplit.indexOf(parent.id);
      if (parentIndex > -1) {
        currentSelectionSplit.splice(parentIndex, 1);
      }
      const children = riskCategoriesConfig.filter((c) => {
        return c.parentId === riskCategory[0].id;
      });
      children.forEach((child) => {
        let childIndex = currentSelectionSplit.indexOf(child.id);
        if (childIndex > -1) {
          currentSelectionSplit.splice(childIndex, 1);
        }
      });
    }
  }

  if (enabled && currentSelectionSplit.indexOf(riskCategoryId) < 0) {
    if (riskCategory[0].parentId) {
      currentSelectionSplit.push(riskCategory[0].id);
    } else {
      currentSelectionSplit.push(riskCategory[0].id);
      let children = riskCategoriesConfig.filter((c) => {
        return c.parentId === riskCategory[0].id;
      });
      children.forEach((child) => {
        currentSelectionSplit.push(child.id);
      });
    }
  }
  return currentSelectionSplit.sort().join(",");
}

function _getRiskCategoryByName(name, property) {
  const riskCategory = riskCategoriesConfig.filter((c) => {
    return c.name === name;
  });
  return riskCategory
    ? property
      ? riskCategory[0][property]
      : riskCategory[0]
    : riskCategory[0];
}
